<template>
	<div
		ref="mainElement"
		class="c-inputText"
		:class="mainClassObj"
		@focus.capture="toggleFocus(true)"
		@blur.capture="toggleFocus(false)">
		<span
			class="c-inputText__label"
			@focus.capture="toggleFocus(true)">{{ label }}</span>
		<input
			v-model="model"
			type="text"
			:name="name"
			:required="required"
			:disabled="disabled"
			:aria-label="label"
			:readonly="readOnly"
			class="c-inputText__input"
			@input="handleInput">
	</div>
</template>

<script>
export default {
	name: 'InputText',
	props: {
		/**
     * The InputText name (html attribute name)
     */
		name: {
			type: String,
			required: false,
			default: () => ''
		},
		/**
     * The InputText label
     */
		label: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The InputText data variable
     */
		data: {
			type: [Number, String],
			required: false,
			default: () => undefined
		},
		/**
     * If the InputText is required (html attribute required)
     * @values true, false
     */
		required: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the InputText is read-only (html attribute readonly)
     * @values true, false
     */
		readOnly: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the InputText is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the InputText is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			model: '',
			isFocused: false
		}
	},
	computed: {
		mainClassObj () {
			return {
				focus: this.isFocused,
				disabled: this.disabled,
				isActive: this.isActive,
				withValue: this.model
			}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				const val = (newVal !== undefined && newVal !== null) ? `${newVal}` : ''
				this.model = val
			},
			immediate: true
		}
	},
	methods: {
		toggleFocus (arg) {
			if (!this.readOnly) {
				this.isFocused = arg
			}
		},
		handleInput () {
			/**
       * Triggers when Checkbox changes it's value
       * @event change
       * @property {number, string} model new value set
       */
			this.$emit('change', this.model)
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-inputText {
  $font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;

  width: 100%;
  border: 1px solid $un-gray-light;
  border-radius: $border-radius;
  background-color: $un-white;
  height: 50px;
  position: relative;

  &::-moz-focus-inner {
    border: 0;
  }

  &.isActive {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;

    .c-inputText__label {
      color: $un-blue;
    }
  }

  &.focus {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;

    .c-inputText__label {
      color: $un-blue;
      top: calc(#{$base-padding} / 2) !important;
      font-size: $font-size-label-top !important;
    }
  }

  &.withValue {
    .c-inputText__label {
      top: calc(#{$base-padding} / 2) !important;
      font-size: $font-size-label-top !important;
    }
  }

  &__label {
    display: block;
    font-family: $primary-font;
    font-size: $font-size-label;
    color: $un-n700;
    position: absolute;
    top: 18px;
    left: calc(#{$base-padding / 2});
    transition: all $default-time $default-easing;
    z-index: 1;
  }

  // Disabled state
  &.disabled {
    border-color: $un-n500;
    background-color: $un-n100;

		.c-inputText__input {
			cursor: not-allowed;
		}

    & .c-inputText__label {
      color: $un-n500;
    }
  }

  &__input {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    background-color: transparent;
    font-family: $primary-font;
    font-size: $font-size-value;
    line-height: $font-size-value;
    color: $un-n700;
    padding: 22px calc(#{$base-padding / 2}) calc(#{$base-padding / 2}) calc(#{$base-padding / 2});
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    z-index: 2;
  }
}
</style>
